import * as React from 'react';
import { useEffect, useState } from 'react';
import { Image as ImageType } from '../../../typings';
import Image from '../Image';

interface ImageWithFocalPointProps {
  image: ImageType;
  aspectRatio: '2x1' | '16x9' | '3x2' | '4x3' | '1x1' | '4x5' | '2x3' | '6x1' | '6x7-promo';
  widthSizes?: string[];
  aspectRatioMobile?: '2x1' | '16x9' | '3x2' | '4x3' | '1x1' | '4x5' | '2x3' | '6x1' | '6x7-promo';
  mobileImageTriggerWidth?: number;
  isLoaded?: Function;
  hasTransparentBackground?: boolean;
  isDarkened?: boolean;
}

const ImageWithFocalPoint = ({
  image,
  aspectRatio,
  aspectRatioMobile,
  mobileImageTriggerWidth,
  widthSizes,
  isLoaded,
  hasTransparentBackground = false,
  isDarkened = false, // passed as percentage 80 etc.
}: ImageWithFocalPointProps) => {
  const [focalPoint, setFocalPoint] = useState<string | null>(null);
  /*
    Set crop quadrant area as:
    'center' | 'top' | 'right' | 'left' | 'bottom' | 'top_right' |
    'top_left' | 'bottom_right' | 'bottom_left';
    Docs:  https://www.contentful.com/developers/docs/references/images-api/#/reference/resizing-&-cropping/specify-focus-area
    We get x / y coords in pixels so we calculate percentage and
    then which area it falls into from there.
  */
  const setFocalArea = () => {
    if (!image.focalPoint) return 'center';
    if (!image.file || !image.file.details) return 'center';

    const xPercentile = (image.focalPoint.x / image.file.details.image.width) * 100;
    const yPercentile = (image.focalPoint.y / image.file.details.image.height) * 100;
    // eslint-disable-next-line no-nested-ternary
    const xQuadrant = xPercentile < 33 ? 'left' : xPercentile > 66 ? 'right' : 'center';
    // eslint-disable-next-line no-nested-ternary
    const yQuadrant = yPercentile < 33 ? 'top' : xPercentile > 66 ? 'bottom' : 'center';

    if (xQuadrant === 'center') {
      return yQuadrant;
    }
    if (yQuadrant === 'center') {
      return xQuadrant;
    }

    return `${yQuadrant}_${xQuadrant}`;
  };

  useEffect(() => {
    if (image.focalPoint && image.file.details) {
      const xPercentile = (image.focalPoint.x / image.file.details.image.width) * 100;
      const yPercentile = (image.focalPoint.y / image.file.details.image.height) * 100;
      setFocalPoint(`${xPercentile}% ${yPercentile}%`);
    }
  }, [image]);

  return (
    <Image
      url={image.file.url}
      alt={image.title}
      focalPoint={focalPoint}
      aspectRatio={aspectRatio}
      widthSizes={widthSizes}
      aspectRatioMobile={aspectRatioMobile}
      mobileImageTriggerWidth={mobileImageTriggerWidth}
      isLoaded={isLoaded}
      hasTransparentBackground={hasTransparentBackground}
      isDarkened={isDarkened}
      cropTo={setFocalArea()}
    />
  );
};

export default ImageWithFocalPoint;
